import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="forgot-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <PrismLogo size={36} />
          <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
        </Link>

        {sent ? (
          <div className="qs-card p-8 text-center" data-testid="forgot-sent">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center">
              <MailCheck className="w-7 h-7" />
            </div>
            <h1 className="font-display text-2xl font-black text-slate-900 mt-4">Check your inbox</h1>
            <p className="mt-2 text-slate-600 text-sm">
              If an account exists for <span className="font-semibold">{email}</span>, we&rsquo;ve sent a link to reset your password.
              The link expires in 1 hour.
            </p>
            <Link to="/login" className="btn-primary mt-6 text-sm inline-flex" data-testid="forgot-back-login">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-black text-slate-900">Forgot password?</h1>
            <p className="mt-1.5 text-slate-500 text-sm">Enter your email and we&rsquo;ll send you a reset link.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-600">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full mt-1.5 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  placeholder="you@school.edu"
                  data-testid="forgot-email"
                />
              </div>
              {err && <p className="text-sm text-rose-600" data-testid="forgot-error">{err}</p>}
              <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-70" data-testid="forgot-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </form>
            <Link to="/login" className="mt-6 text-sm text-indigo-600 font-semibold hover:underline inline-flex items-center gap-1" data-testid="forgot-cancel">
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
